import type { AppState, ScenarioState, VectorLayerModel } from './types';

/**
 * V1.9.0 E5 · global Deshacer / Rehacer.
 *
 * The store is immutable, so one step keeps the previous and the next VALUE (a reference, never a copy) of the project
 * parts an action changed. Only document edits are undoable: layers and planning objects, classification and legend,
 * labels, style, scenarios, Áreas parciales, project fields and Excel relations, saved selections, presentation and
 * the Maestro's geometry edits. Navigation and view (selection, Ver por, ámbito, filtros, mapa base, paneles) are not.
 *
 * One user action is one step: the derived updates a store listener makes while the action is being applied (for
 * example the classification refreshed after an Área parcial) join the same step. Steps that come from a continuous
 * control (a slider, typing) merge while the user keeps adjusting the same thing. Loading a Maestro, opening or
 * creating a project start a new history. The geometry editing session and the vertex editor keep their own local
 * undo while they are open (see HistoryController).
 */
export const UNDOABLE_KEYS: ReadonlySet<keyof AppState> = new Set<keyof AppState>([
  'model', 'sourceModel', 'geometryEdits', 'layers', 'labelMode', 'labelConfig', 'styleConfig', 'classificationConfig', 'thematicSchemes',
  'legendConfig', 'savedSelections', 'presentationWorkspace', 'activePresentationPreset', 'operationalFields', 'excelJoins', 'subareas', 'scenarios',
  'subareaLabelsVisible',
]);

export const HISTORY_LIMIT = 100;

/**
 * View choices kept inside an undoable part (V1.9.0 E2, approved): which scenario is shown (Actual / Propuesta) and
 * where its mark goes. Changing them is not a step. When a step is undone or redone, the choice the user made after
 * the step is kept; if it was not touched since, the step's own choice is restored, so undo/redo stay exact.
 * `values` is the side being restored, `other` the side being left (after for an undo, before for a redo).
 */
export function keepViewChoices(values: Partial<AppState>, other: Partial<AppState>, state: AppState): Partial<AppState> {
  const restored = values.scenarios as ScenarioState | undefined; const left = other.scenarios as ScenarioState | undefined;
  if (!restored || !left) return values;
  const current = state.scenarios;
  // «Actual» (null) always exists; a shown proposal must still exist in the restored scenarios.
  const viewChanged = current.active !== left.active && (current.active === null || restored.items.some(({ id }) => id === current.active));
  const active = viewChanged ? current.active : restored.active;
  const mark = current.mark !== left.mark ? current.mark : restored.mark;
  const { mark: _restoredMark, ...rest } = restored;
  return { ...values, scenarios: { ...rest, active, ...(mark ? { mark } : {}) } };
}
/** A continuous control (slider, typing) merges into the previous step while it is adjusted within this time. */
export const MERGE_WINDOW_MS = 1500;

type Values = Partial<Record<keyof AppState, unknown>>;

/**
 * The Maestro layer carries its active model (`masterModel`), DERIVED from the source model and the geometry edits:
 * each geometry edit makes a new one (new fragment lists, indices and entities, ~O(fragments)). A step that records
 * `layers` would keep that version alive for as long as the step lives, so layers are recorded without it and the
 * model is attached again when the step is applied (App.completeHistoryPatch). Same input → same copy, so the
 * `after` of one step and the `before` of the next still share it.
 */
const detachedLayers = new WeakMap<readonly VectorLayerModel[], readonly VectorLayerModel[]>();
export function withoutDerivedModels(layers: readonly VectorLayerModel[]): readonly VectorLayerModel[] {
  if (!layers.some((layer) => layer.masterModel)) return layers;
  let detached = detachedLayers.get(layers);
  if (!detached) {
    detached = layers.map((layer) => { if (!layer.masterModel) return layer; const { masterModel: _derived, ...rest } = layer; return rest; });
    detachedLayers.set(layers, detached);
  }
  return detached;
}
const recorded = (key: keyof AppState, value: unknown): unknown => (key === 'layers' ? withoutDerivedModels(value as readonly VectorLayerModel[]) : value);

export interface HistoryEntry {
  readonly keys: readonly (keyof AppState)[];
  readonly before: Values;
  readonly after: Values;
  readonly label: string;
  readonly at: number;
  /** Recorded from a continuous control: a following continuous change of the same keys merges into it. */
  readonly continuous: boolean;
}

/** What a step changed, as the user names it («Deshacer: clasificación»). The first matching part wins. */
const LABELS: readonly [keyof AppState, string][] = [
  ['geometryEdits', 'edición de geometría'], ['scenarios', 'escenario'], ['subareas', 'áreas parciales'], ['classificationConfig', 'clasificación'],
  ['thematicSchemes', 'clasificación'], ['legendConfig', 'leyenda'], ['labelConfig', 'etiquetas'], ['labelMode', 'etiquetas'], ['styleConfig', 'estilo del Maestro'],
  ['operationalFields', 'campos del proyecto'], ['excelJoins', 'relación con Excel'], ['savedSelections', 'selecciones guardadas'],
  ['presentationWorkspace', 'presentación'], ['activePresentationPreset', 'presentación'], ['subareaLabelsVisible', 'áreas parciales'], ['layers', 'capas y objetos'],
];
export const historyLabel = (keys: readonly (keyof AppState)[]): string => LABELS.find(([key]) => keys.includes(key))?.[1] ?? 'cambio';

export interface PendingStep { readonly before: Values; readonly keys: Set<keyof AppState>; readonly continuous: boolean }

export class EditHistory {
  private past: HistoryEntry[] = [];
  private future: HistoryEntry[] = [];
  private readonly listeners = new Set<() => void>();

  constructor(private readonly now: () => number = () => Date.now(), private readonly limit = HISTORY_LIMIT) {}

  get canUndo(): boolean { return this.past.length > 0; }
  get canRedo(): boolean { return this.future.length > 0; }
  get undoLabel(): string | null { return this.past.at(-1)?.label ?? null; }
  get redoLabel(): string | null { return this.future.at(-1)?.label ?? null; }
  get size(): number { return this.past.length; }

  subscribe(listener: () => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  private notify(): void { this.listeners.forEach((listener) => listener()); }

  /** Closes a step opened by the store: keeps the parts that really changed, merging a continuous adjustment. */
  commit(step: PendingStep, state: AppState): void {
    const keys = [...step.keys].filter((key) => step.before[key] !== state[key]);
    if (!keys.length) return;
    const after: Values = Object.fromEntries(keys.map((key) => [key, recorded(key, state[key])]));
    const before: Values = Object.fromEntries(keys.map((key) => [key, recorded(key, step.before[key])]));
    const at = this.now(); const last = this.past.at(-1);
    if (step.continuous && last?.continuous && !this.future.length && at - last.at <= MERGE_WINDOW_MS && sameKeys(last.keys, keys)) {
      this.past[this.past.length - 1] = { ...last, after, at };
    } else {
      this.past.push({ keys, before, after, label: historyLabel(keys), at, continuous: step.continuous });
      if (this.past.length > this.limit) this.past.shift();
    }
    this.future = [];
    this.notify();
  }

  /** The step to undo (removed from the past; the caller applies its `before` and then calls `undone`). */
  takeUndo(): HistoryEntry | null { return this.past.pop() ?? null; }
  undone(entry: HistoryEntry): void { this.future.push(entry); this.notify(); }
  takeRedo(): HistoryEntry | null { return this.future.pop() ?? null; }
  redone(entry: HistoryEntry): void { this.past.push(entry); this.notify(); }

  clear(): void { if (!this.past.length && !this.future.length) return; this.past = []; this.future = []; this.notify(); }
}

const sameKeys = (a: readonly (keyof AppState)[], b: readonly (keyof AppState)[]): boolean => a.length === b.length && a.every((key) => b.includes(key));
